import { type RefObject, useLayoutEffect, useState } from "react";

// Viewport Y of the bottom edge of a flex-wrap grid's last row — used to
// align a sidebar element (e.g. the catalog brand picker) so it visually
// ends on the same line as the product cards, instead of an arbitrary fixed
// height. Returns null below `minWidth` (where the sidebar typically stacks
// above the grid instead of sitting beside it, so this alignment wouldn't
// mean anything) or when the grid has no rendered cards.
export default function useGridLastRowBottom(
  gridRef: RefObject<HTMLElement | null>,
  deps: unknown[],
  minWidth = 1024
): number | null {
  const [bottomY, setBottomY] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const measure = () => {
      if (window.innerWidth < minWidth) {
        setBottomY(null);
        return;
      }
      const cards = Array.from(el.children) as HTMLElement[];
      if (cards.length === 0) {
        setBottomY(null);
        return;
      }
      const rects = cards.map((c) => c.getBoundingClientRect());
      const lastRowTop = Math.max(...rects.map((r) => r.top));
      const lastRowBottom = Math.max(
        ...rects.filter((r) => Math.abs(r.top - lastRowTop) < 1).map((r) => r.bottom)
      );
      setBottomY(lastRowBottom);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return bottomY;
}
